import { Box, Chip, Modal, Paper, Typography } from '@mui/material';
import { Close, ImageNotSupported } from '@mui/icons-material';
import { YardSpotSelection } from './YardChecksSpotGrid';
import {
  formatConfidence,
  formatTimestamp,
  rtlsPreviewImage,
  verdictColor
} from './utils';
import { formatAge, freshnessBucketFor } from './freshness';
import { cameraHeading } from './geo';
import YardSpotMap from './YardSpotMap';
import { YardGeometryProps } from 'api/yardGeometry';

interface YardSpotDetailModalProps {
  selection: YardSpotSelection | null;
  geometry: GeoJSON.FeatureCollection<GeoJSON.Geometry, YardGeometryProps> | null;
  now: Date;
  onClose: () => void;
}

interface DetailRow {
  label: string;
  value: string;
}

const YardSpotDetailModal = ({
  selection,
  geometry,
  now,
  onClose
}: YardSpotDetailModalProps) => {
  if (!selection) return null;
  const { zone, spot } = selection;
  const history = spot.latestHistory;
  const bucket = freshnessBucketFor(history?.timestamp, now);

  const imageUrl = rtlsPreviewImage(history);

  // Occupancy fields come from trailer_lineage, not rtls_history — that data
  // source isn't wired up yet, so these are templated out but always "-" for now.
  const occupancyRows: DetailRow[] = [
    { label: 'Status', value: '-' },
    { label: 'Trailer', value: '-' },
    { label: 'SCAC', value: '-' },
    { label: 'Carrier', value: '-' },
    { label: 'Time at spot', value: '-' },
    { label: 'Arrived', value: '-' }
  ];

  const rtlsRows: DetailRow[] = history
    ? [
        { label: 'Trailer # (OCR)', value: history.trailerNumberOcrReading || '—' },
        { label: 'Trailer # (expected)', value: history.trailerNumberExpected || '—' },
        {
          label: 'OCR confidence',
          value: formatConfidence(history.trailerNumberOcrConfidenceScore)
        },
        { label: 'Brand (OCR)', value: history.trailerBrandOcrReading || '—' },
        { label: 'Brand (expected)', value: history.trailerBrandExpected || '—' },
        { label: 'Expected match', value: history.expectedMatch || '—' },
        { label: 'Match confidence', value: formatConfidence(history.matchConfidence) },
        { label: 'Position source', value: history.positionSource || '—' },
        { label: 'Last good read', value: `${formatAge(history.timestamp, now)} ago` },
        { label: 'Read at', value: formatTimestamp(history.timestamp) }
      ]
    : [];

  return (
    <Modal open={Boolean(selection)} onClose={onClose}>
      <Box
        onClick={(event) => {
          // The MUI Backdrop that normally handles click-outside-to-close sits
          // at zIndex: -1 relative to this Box, so it's fully occluded by it —
          // handle the click ourselves, but only when it lands on this Box
          // directly (the empty padding around the Paper), not something inside it.
          if (event.target === event.currentTarget) onClose();
        }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          p: 2
        }}
      >
        <Paper sx={{ width: 'min(720px, 100%)', maxHeight: '88vh', overflow: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              px: 3,
              pt: 2.5,
              pb: 2,
              borderBottom: 1,
              borderColor: 'divider'
            }}
          >
            <Box>
              <Typography variant="h6">Spot {spot.yardSpotName}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                {zone.facilityZoneName || 'Unassigned'}
              </Typography>
            </Box>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Chip
                label={`${bucket.label} · ${formatAge(history?.timestamp, now)}`}
                size="small"
                sx={{
                  bgcolor: bucket.color,
                  // Yellow (stale) is too light for white text to stay legible.
                  color: bucket.key === 'stale' ? 'rgba(0, 0, 0, 0.87)' : '#fff',
                  fontWeight: 500
                }}
              />
              <Close
                sx={{ cursor: 'pointer', color: 'text.secondary' }}
                onClick={onClose}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 3,
              px: 3,
              py: 2.5
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {occupancyRows.map((row) => (
                <Box
                  key={row.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1.5,
                    py: 1.125,
                    borderBottom: 1,
                    borderColor: 'divider'
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      minWidth: 116
                    }}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    sx={{
                      ml: 'auto',
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: 'right',
                      color: 'text.disabled'
                    }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              ))}

              {!history ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                  No RTLS history for this spot yet.
                </Typography>
              ) : (
                <>
                  {history.verdict && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1.125,
                        borderBottom: 1,
                        borderColor: 'divider'
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          textTransform: 'uppercase',
                          color: 'text.secondary',
                          minWidth: 116
                        }}
                      >
                        Verdict
                      </Typography>
                      <Chip
                        label={history.verdict}
                        size="small"
                        color={verdictColor(history.verdict)}
                        sx={{ ml: 'auto' }}
                      />
                    </Box>
                  )}
                  {rtlsRows.map((row) => (
                    <Box
                      key={row.label}
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 1.5,
                        py: 1.125,
                        borderBottom: 1,
                        borderColor: 'divider'
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          textTransform: 'uppercase',
                          color: 'text.secondary',
                          minWidth: 116
                        }}
                      >
                        {row.label}
                      </Typography>
                      <Typography
                        sx={{
                          ml: 'auto',
                          fontSize: 14,
                          fontWeight: 500,
                          textAlign: 'right'
                        }}
                      >
                        {row.value}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{ textTransform: 'uppercase', color: 'text.secondary' }}
              >
                Last image
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  aspectRatio: '4 / 3',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt="RTLS capture"
                    style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      color: 'text.disabled'
                    }}
                  >
                    <ImageNotSupported fontSize="large" />
                    <Typography variant="caption" sx={{ mt: 0.5 }}>
                      No image available
                    </Typography>
                  </Box>
                )}
              </Box>
              {imageUrl && (
                <Box sx={{ mt: 0.5, textAlign: 'right' }}>
                  <a
                    href={imageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 13 }}
                  >
                    View full image
                  </a>
                </Box>
              )}

              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  mt: 2,
                  display: 'block'
                }}
              >
                Location
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  height: 220,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                  overflow: 'hidden'
                }}
              >
                <YardSpotMap
                  lat={history?.lat ?? null}
                  lon={history?.lon ?? null}
                  heading={
                    history?.deviceHeading != null
                      ? cameraHeading(history.deviceHeading, history.deviceOrientation)
                      : null
                  }
                  geometry={geometry}
                  spotUuid={spot.yardSpotUuid}
                  spotName={spot.yardSpotName}
                  zoneName={zone.facilityZoneName}
                />
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

export default YardSpotDetailModal;
